if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.buyNowButton = this.querySelector('[data-buy-now-button]');
        if (this.buyNowButton) {
          this.buyNowSpinner = this.buyNowButton.querySelector('.loading__spinner');
          this.buyNowButton.addEventListener('click', this.onBuyNowHandler.bind(this));
        }
      }

      // ONE-PLAN RULE: empties the cart before adding, so the cart only ever holds one plan.
      clearCart() {
        const shopifyRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        return fetch(`${shopifyRoot}cart/clear.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        });
      }

      // Shopify's native dynamic-checkout button only ever submits the form's single
      // variant, with no way to include the add-on line items we add via AJAX, so a
      // shopper who picked an add-on and hit it would be charged for the base plan only.
      // This button instead runs the same add-to-cart request as onSubmitHandler
      // (base variant + selected add-ons) and then sends the shopper straight to checkout.
      onBuyNowHandler(evt) {
        evt.preventDefault();
        if (this.buyNowButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.buyNowButton.setAttribute('aria-disabled', true);
        this.buyNowButton.classList.add('loading');
        if (this.buyNowSpinner) this.buyNowSpinner.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const planAddons =
          this.closest('product-info')?.querySelector('plan-addons') ||
          document.querySelector('product-info plan-addons');
        const selectedAddons = planAddons ? planAddons.selectedAddons : [];
        const mainVariantId = this.variantIdInput.value;
        const bundleId = `${mainVariantId}-${Math.random().toString(36).slice(2)}`;

        config.body = JSON.stringify({
          items: [
            {
              id: mainVariantId,
              quantity: 1,
              properties: selectedAddons.length ? { _bundle_id: bundleId } : undefined,
            },
            ...selectedAddons.map((addon) => ({
              id: Number(addon.id),
              quantity: addon.quantity,
              properties: { _addon_of: bundleId },
            })),
          ],
        });

        this.clearCart()
          .then(() => fetch(`${routes.cart_add_url}`, config))
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: mainVariantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              return;
            }

            const shopifyRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
            window.location.href = `${shopifyRoot}checkout`;
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.buyNowButton.classList.remove('loading');
            this.buyNowButton.removeAttribute('aria-disabled');
            if (this.buyNowSpinner) this.buyNowSpinner.classList.add('hidden');
          });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        // The sticky add-to-cart bar clones this form outside of <product-info>, so
        // this.closest('product-info') resolves to null there. Fall back to the live
        // plan-addons on the page so the sticky bar picks up the same addon selection
        // as the original form.
        const planAddons =
          this.closest('product-info')?.querySelector('plan-addons') ||
          document.querySelector('product-info plan-addons');
        const selectedAddons = planAddons ? planAddons.selectedAddons : [];
        const mainVariantId = this.variantIdInput.value;

        if (selectedAddons.length) {
          if (this.cart) this.cart.setActiveElement(document.activeElement);
          const bundleId = `${mainVariantId}-${Math.random().toString(36).slice(2)}`;
          config.body = JSON.stringify({
            items: [
              {
                id: mainVariantId,
                quantity: 1,
                properties: { _bundle_id: bundleId },
              },
              ...selectedAddons.map((addon) => ({
                id: Number(addon.id),
                quantity: addon.quantity,
                properties: { _addon_of: bundleId },
              })),
            ],
            sections: this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : [],
            sections_url: window.location.pathname,
          });
        } else {
          delete config.headers['Content-Type'];
          const formData = new FormData(this.form);
          formData.set('quantity', '1');
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;
        }

        this.clearCart()
          .then(() => fetch(`${routes.cart_add_url}`, config))
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: mainVariantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: mainVariantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}